import { Box, Chip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { palette, spaces, typography } from '../../assets/styles/themes/tokens';
import type { Collection } from '../../types/AppContent';
import timeSinceLastUpdate from '../../utils/timeSinceLastUpdate';
import useResponsivePadding from '../../utils/useResponsivePadding';
import CardDescription from '../cards/common/CardDescription/NewCardDescription';
import CustomMuiCard from '../cards/MuiCard/CustomMuiCard/CustomMuiCard';
import CuratedCollectionOverviewFollowButton from './CuratedCollectionOverviewFollowButton';

interface FollowedCollectionOverviewHeaderProps {
	collection: Collection;
	collectionStatus: AsyncActionStatus;
}

const CuratedCollectionOverviewHeader = ({
	collection,
	collectionStatus,
}: FollowedCollectionOverviewHeaderProps) => {
	const { t } = useTranslation();
	const cardPadding = useResponsivePadding();
	const [isExpanded, setIsExpanded] = useState(false);

	if (!collection && collectionStatus !== AsyncActionStatus.READY) return null;

	return (
		<CustomMuiCard
			title={collection.title}
			subheader={`${collection.contents.length} ${t('Contents')}`}
			color={collection.color}
			action={<CuratedCollectionOverviewFollowButton collection={collection} />}
			sxCardStyle={{
				main: {
					border: 'none',
					borderRadius: 'unset',
				},
				header: {
					padding: `${cardPadding} ${cardPadding} ${spaces.md} ${cardPadding}`,
				},
				title: {
					...typography.h5,
					color: palette.text.primary,
				},
				content: {
					padding: `${spaces.md} ${cardPadding} ${cardPadding} ${cardPadding}`,
					'&:last-child': { paddingBottom: cardPadding },
				},
				avatar: { '& button': { margin: 0 } },
			}}
		>
			<CardDescription
				description={collection.description}
				isExpanded={isExpanded}
				onShowToggleClick={() => setIsExpanded(!isExpanded)}
			/>
			<Box>
				{collection?.subjects?.map((chip) => (
					<Chip
						key={chip}
						label={chip}
						variant="outlined"
						color="default"
						size="small"
						sx={{
							marginRight: spaces.xs,
							marginBottom: spaces.xs,
							border: `1px solid ${palette.components.chip.defaultEnabled}`,
							height: 'auto',
						}}
					/>
				))}
			</Box>
			<Box>
				{collection.ownerData && (
					<Typography variant="caption" sx={{ color: palette.text.primary }}>
						{`${collection.ownerData.name} • `}
					</Typography>
				)}
				<Typography variant="caption" sx={{ color: palette.text.primary }}>
					{timeSinceLastUpdate(collection.changed)} {t('Updated (date)')}
				</Typography>
			</Box>
		</CustomMuiCard>
	);
};

export default CuratedCollectionOverviewHeader;
